import { useEffect, useMemo, useRef, useState } from "react";
import {
    ConfigProvider, Select, Card, Table, Space, Button, Tag, Typography, Empty, Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import {
    PrinterOutlined, FileExcelOutlined, ReloadOutlined, PieChartOutlined, BankOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import entete from "../../../assets/img/entete_pool.png";
import BarreTop from "../../../components/docs/BarreTop";
import { DocumentFooter } from "../../../components/docs/DocumentFooter";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

// Filtre persistant (branche) — même convention que les autres écrans v2.
const readSS = (k, def) => {
    try { const v = sessionStorage.getItem(k); return v !== null ? JSON.parse(v) : def; } catch { return def; }
};

// Formatage d'un pourcentage FR (jusqu'à 2 décimales, sans zéros inutiles).
const fmtPct = (v) => (v == null
    ? "—"
    : `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`);

/**
 * CONSULTATION des CLÉS DE RÉTROCESSION par branche (menu Cession & Rétrocession) : matrice
 * EXERCICES × COMPAGNIES membres du Pool (une clé = %), total 100 % par exercice. Lecture seule,
 * imprimable (A4 paysage) et exportable en Excel — selon le modèle demandé.
 */
const ClesRetrocessionConsultation = () => {
    const [cles, setCles] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [branche, setBranche] = useState(() => readSS("clesRetro_branche", null));
    const printRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `@page { size: A4 landscape; margin: 12mm; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`,
        documentTitle: "Cles_de_retrocession",
    });

    useEffect(() => {
        axiosInstance.get("/api/branche")
            .then((res) => {
                const list = res.data || [];
                setBranches(list);
                // Défaut : la branche persistée si encore valide, sinon TPV, sinon la première.
                if (!branche && list.length) {
                    const tpv = list.find((b) => /tpv/i.test(b?.codeBranche || "") || /tpv/i.test(b?.nom || ""));
                    setBranche((tpv || list[0])?.codeBranche || null);
                }
            })
            .catch(() => toast.error("Erreur lors du chargement des branches"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        sessionStorage.setItem("clesRetro_branche", JSON.stringify(branche ?? null));
        if (branche) chargerCles(branche);
        else setCles([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branche]);

    const chargerCles = (codeBranche) => {
        setLoading(true);
        axiosInstance.get("/api/cle-repartition", { params: { branche: codeBranche } })
            .then((res) => setCles(res.data || []))
            .catch(() => toast.error("Erreur lors du chargement des clés"))
            .finally(() => setLoading(false));
    };

    const brancheLabel = useMemo(() => {
        const b = branches.find((x) => x.codeBranche === branche);
        return b?.nom || b?.codeBranche || branche || "—";
    }, [branches, branche]);

    // PIVOT : lignes = exercices, colonnes = compagnies, cellule = clé (%). En cas de plusieurs clés
    // pour un même (exercice, compagnie) — périodes de validité successives — on retient la PLUS
    // RÉCENTE (dateDebut max).
    const matrix = useMemo(() => {
        const exMap = new Map();   // exerciceId -> { exercice, cells: {compCode: {cle, dateDebut}} }
        const compMap = new Map(); // compCode -> compagnie
        (cles || []).forEach((c) => {
            const exId = c?.exercice?.exerciceId;
            const compCode = c?.compagnie?.code;
            if (exId == null || !compCode) return;
            if (!exMap.has(exId)) exMap.set(exId, { exercice: c.exercice, cells: {} });
            const cell = exMap.get(exId).cells[compCode];
            const dd = c?.dateDebut || "";
            if (!cell || dd >= cell.dateDebut) {
                exMap.get(exId).cells[compCode] = { cle: c?.cle ?? 0, dateDebut: dd };
            }
            compMap.set(compCode, c.compagnie);
        });
        // Ordre des compagnies = ordre du CODE de la compagnie (tri naturel : 2 avant 10).
        const compagnies = [...compMap.values()].sort((a, b) =>
            String(a?.code || "").localeCompare(String(b?.code || ""), undefined, { numeric: true }));
        const rows = [...exMap.values()]
            .map((r) => {
                const total = compagnies.reduce((s, cp) => s + (r.cells[cp.code]?.cle || 0), 0);
                return { exercice: r.exercice, cells: r.cells, total };
            })
            .sort((a, b) => (a.exercice?.libelleExercice || "")
                .localeCompare(b.exercice?.libelleExercice || "", undefined, { numeric: true }));
        return { compagnies, rows };
    }, [cles]);

    const columns = useMemo(() => {
        const cols = [
            {
                title: "Exercice", key: "exercice", fixed: "left", width: 120,
                render: (_, r) => <Text strong>{r.exercice?.libelleExercice || "—"}</Text>,
            },
            ...matrix.compagnies.map((cp) => ({
                title: (
                    <Tooltip title={cp?.raisonSociale}>
                        <span>{cp?.raisonSociale || cp?.code}</span>
                    </Tooltip>
                ),
                key: cp.code, align: "center", width: 130,
                render: (_, r) => {
                    const v = r.cells[cp.code]?.cle;
                    return v == null ? <Text type="secondary">—</Text> : <span style={{ fontWeight: 600 }}>{fmtPct(v)}</span>;
                },
            })),
            {
                title: "Total", key: "total", align: "center", fixed: "right", width: 110,
                render: (_, r) => {
                    const ok = Math.abs((r.total || 0) - 100) < 0.01;
                    return <Tag color={ok ? "green" : "red"} style={{ fontWeight: 700 }}>{fmtPct(r.total)}</Tag>;
                },
            },
        ];
        return cols;
    }, [matrix]);

    const exporterExcel = () => {
        if (!matrix.rows.length) { toast.warn("Aucune donnée à exporter"); return; }
        const header = ["Exercice", ...matrix.compagnies.map((c) => c?.raisonSociale || c?.code), "Total (%)"];
        const dataRows = matrix.rows.map((r) => [
            r.exercice?.libelleExercice || "",
            ...matrix.compagnies.map((c) => (r.cells[c.code]?.cle ?? null)),
            Number((r.total || 0).toFixed(2)),
        ]);
        const aoa = [
            [`Clés de répartition par compagnie — membres du Pool`],
            [`Branche : ${brancheLabel}`],
            [],
            header,
            ...dataRows,
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Clés");
        XLSX.writeFile(wb, `Cles_retrocession_${(brancheLabel || "").replace(/\s+/g, "_")}.xlsx`);
    };

    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                    <SideBar currentMenu={"OPERATIONS"} />
                    <div className="layout-page">
                        <NavBar />
                        <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                            <div className="container-xxl flex-grow-1 container-p-y">

                                {/* En-tête */}
                                <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                    <div>
                                        <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                            <PieChartOutlined style={{ marginRight: 8 }} />Clés de rétrocession par branche
                                        </Title>
                                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                            Répartition (%) par compagnie membre du Pool et par exercice — total 100 % par exercice
                                        </Text>
                                    </div>
                                    <Space wrap>
                                        <Tooltip title="Actualiser"><Button icon={<ReloadOutlined />} onClick={() => branche && chargerCles(branche)} /></Tooltip>
                                        <Button icon={<FileExcelOutlined />} onClick={exporterExcel}
                                            style={{ borderRadius: 8, fontWeight: 600 }}>Exporter (Excel)</Button>
                                        <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}
                                            style={{ background: "#fff", color: PRIMARY, borderRadius: 8, fontWeight: 700, border: "none" }}>Imprimer</Button>
                                    </Space>
                                </div>

                                {/* Sélecteur de branche */}
                                <Card style={{ ...cardStyle, marginBottom: 18 }} bodyStyle={{ padding: 16 }}>
                                    <Space wrap align="center">
                                        <Text strong><BankOutlined /> Branche :</Text>
                                        <Select showSearch optionFilterProp="label" style={{ minWidth: 260 }}
                                            placeholder="Sélectionner une branche" value={branche || undefined}
                                            onChange={(v) => setBranche(v || null)}
                                            options={branches.map((b) => ({ value: b.codeBranche, label: b.nom || b.codeBranche }))} />
                                    </Space>
                                </Card>

                                {/* Tableau (interactif) */}
                                <Card style={cardStyle} bodyStyle={{ padding: 8 }}>
                                    <Table
                                        rowKey={(r) => r.exercice?.exerciceId}
                                        loading={loading}
                                        columns={columns}
                                        dataSource={matrix.rows}
                                        size="small"
                                        scroll={{ x: "max-content" }}
                                        pagination={false}
                                        locale={{ emptyText: <Empty description="Aucune clé pour cette branche" /> }}
                                    />
                                </Card>

                                {/* Zone imprimable (modèle) */}
                                <div style={{ position: "absolute", left: -99999, top: 0 }} aria-hidden>
                                    <div ref={printRef} style={{ padding: "8mm 10mm", fontFamily: "Arial, sans-serif", color: "#000", background: "#fff" }}>
                                        {/* En-tête USUEL des documents PoolIns (entête + barre navy) */}
                                        <div style={{ textAlign: "center", marginBottom: 4 }}>
                                            <img src={entete} alt="Pool TPVM-VT GIE" style={{ width: "100%", height: 95, objectFit: "contain" }} />
                                        </div>
                                        <BarreTop />
                                        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: 15, textDecoration: "underline", marginBottom: 2 }}>
                                            Les clés de répartition par compagnie — membres du Pool
                                        </div>
                                        <div style={{ textAlign: "center", fontSize: 13, marginBottom: 10 }}>
                                            Branche : <b>{brancheLabel}</b>
                                        </div>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                                            <thead>
                                                <tr style={{ background: "#eef0f7" }}>
                                                    <th style={cellTh}>Exercice</th>
                                                    {matrix.compagnies.map((c) => (
                                                        <th key={c.code} style={cellTh}>{c?.raisonSociale || c?.code}</th>
                                                    ))}
                                                    <th style={cellTh}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matrix.rows.map((r) => (
                                                    <tr key={r.exercice?.exerciceId}>
                                                        <td style={{ ...cellTd, fontWeight: "bold" }}>{r.exercice?.libelleExercice}</td>
                                                        {matrix.compagnies.map((c) => (
                                                            <td key={c.code} style={{ ...cellTd, textAlign: "center" }}>{fmtPct(r.cells[c.code]?.cle)}</td>
                                                        ))}
                                                        <td style={{ ...cellTd, textAlign: "center", fontWeight: "bold" }}>{fmtPct(r.total)}</td>
                                                    </tr>
                                                ))}
                                                {matrix.rows.length === 0 && (
                                                    <tr><td style={cellTd} colSpan={matrix.compagnies.length + 2}>Aucune donnée</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {/* Pied USUEL des documents PoolIns (bandeau des compagnies membres) */}
                                        <div style={{ marginTop: 18 }}>
                                            <DocumentFooter />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer />
                            <div className="content-backdrop fade"></div>
                        </div>
                    </div>
                </div>
                <div className="layout-overlay layout-menu-toggle"></div>
                <div className="drag-target"></div>
            </div>
        </ConfigProvider>
    );
};

const cellTh = { border: "1px solid #000", padding: "5px 8px", textAlign: "center", fontWeight: "bold" };
const cellTd = { border: "1px solid #000", padding: "4px 8px" };

export default ClesRetrocessionConsultation;
